import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, MessageCircle, Sparkles, Star, Clock, ArrowRight, Warehouse, ShieldCheck, Truck } from "lucide-react";
import { api, isNewProduct, NEW_PRODUCT_DAYS } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PromoBanner from "@/components/PromoBanner";
import Brands from "@/components/Brands";
import { CategoryChips } from "@/components/CategoriesMenu";
import { useActiveSeller } from "@/hooks/useActiveSeller";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "featured", label: "Destacados", icon: Star },
  { key: "new", label: "Novedades", icon: Sparkles },
  { key: "recent", label: "Recién llegados", icon: Clock },
];

export default function Home() {
  const [products, setProducts] = useState(null);
  const [tab, setTab] = useState("featured");
  const { seller, whatsapp } = useActiveSeller();
  const { user } = useAuth();

  const consultMsg = seller
    ? `Hola ${seller.name}! Quería hacerte una consulta sobre los productos.`
    : "Hola! Quería hacer una consulta sobre sus productos.";
  const wholesaleMsg = seller
    ? `Hola ${seller.name}! Soy comerciante y me interesa recibir precios mayoristas.`
    : "Hola! Soy comerciante y me interesa recibir precios mayoristas.";

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const lists = useMemo(() => {
    const all = products || [];
    return {
      featured: all.filter((p) => p.featured).slice(0, 10),
      new: all.filter(isNewProduct).slice(0, 10),
      recent: all.slice(0, 10),
    };
  }, [products]);

  const visible = lists[tab];

  return (
    <div data-testid="home-page">
      {/* HERO STRIP */}
      <section className="bg-white border-b border-slate-200 relative overflow-hidden" data-testid="hero-strip">
        <div className="absolute inset-y-0 right-0 w-1/2 hero-pattern pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 grid md:grid-cols-[1fr_minmax(280px,400px)] gap-6 md:gap-10 items-center relative">
          <div className="fade-in">
            <img
              src="/logo.png?v=5"
              alt="Produlimp — Limpieza & Bazar"
              className="h-20 sm:h-24 md:h-28 w-auto object-contain"
              data-testid="hero-logo"
            />
            <h1 className="font-heading font-bold text-slate-900 text-xl sm:text-2xl lg:text-[1.75rem] leading-snug tracking-tight mt-5 max-w-2xl" data-testid="hero-tagline">
              Simplificá tus pedidos: la forma más eficiente y rápida de cargar tu pedido. Todo en un solo lugar.
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/shop" className="btn-primary text-base px-7 py-3.5 group" data-testid="hero-order-cta">
                <ShoppingCart className="w-5 h-5 transition-transform group-hover:-rotate-12" /> Cargá tu pedido
              </Link>
              <a
                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(consultMsg)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-green-700 px-3 py-2 transition-colors"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 text-green-600" /> {seller ? `Consultar a ${seller.name.split(" ")[0]}` : "Consultar por WhatsApp"}
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-green-600" /> Envíos a todo el país</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-blue-600" /> +3500 productos</span>
              <span className="inline-flex items-center gap-1.5"><Warehouse className="w-4 h-4 text-red-500" /> Depósito propio</span>
            </div>
          </div>
          <div className="relative fade-in hidden md:block" style={{ animationDelay: "120ms" }}>
            <img
              src="/store.jpg?v=2"
              alt="Depósito Produlimp"
              className="rounded-2xl shadow-lg object-cover w-full h-52 lg:h-60"
              data-testid="hero-image"
            />
            <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-xl shadow px-3 py-2 flex items-center gap-2 text-xs font-semibold text-slate-800">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Nuestro depósito · Atención personalizada
            </div>
          </div>
        </div>
      </section>

      {/* PROMO STRIP */}
      <PromoBanner />

      {/* PRODUCTS FIRST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10" data-testid="home-products">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-green-700">Catálogo</span>
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight mt-1">Elegí y sumá a tu pedido</h2>
            {!user && <p className="text-sm text-slate-500 mt-1">Ingresá o registrate para ver los precios y cargar tu pedido.</p>}
          </div>
          <div className="inline-flex bg-slate-100 rounded-full p-1 self-start lg:self-auto" role="tablist" data-testid="product-tabs">
            {TABS.map((t) => {
              const n = lists[t.key].length;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={`inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === t.key ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
                  data-testid={`product-tab-${t.key === "featured" ? "destacados" : t.key === "new" ? "novedades" : "recientes"}`}
                >
                  <t.icon className={`w-3.5 h-3.5 ${t.key === "new" ? "text-green-600" : t.key === "featured" ? "text-red-500" : "text-blue-600"}`} />
                  {t.label}
                  {t.key === "new" && n > 0 && <span className="ml-0.5 text-[10px] font-bold bg-green-600 text-white rounded-full px-1.5 py-0.5">{n}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-6"><CategoryChips /></div>

        {products === null ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {Array.from({ length: 10 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-14 bg-white rounded-2xl border border-slate-200" data-testid="home-products-empty">
            <p className="text-slate-600">{tab === "new" ? `No hay productos nuevos en los últimos ${NEW_PRODUCT_DAYS} días.` : "Todavía no hay productos en esta sección."}</p>
            <Link to="/shop" className="mt-3 inline-flex items-center gap-1 text-blue-600 font-semibold hover:underline">Ver toda la tienda <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6" data-testid="home-products-grid">
            {visible.map((p, i) => (
              <div key={`${tab}-${p.id}`} className="fade-in" style={{ animationDelay: `${Math.min(i, 9) * 50}ms` }}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/shop" className="btn-outline" data-testid="home-view-all-btn">Ver toda la tienda <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* BRANDS */}
      <Brands />

      {/* WHOLESALE CTA */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="rounded-3xl bg-slate-900 p-8 md:p-12 text-white relative overflow-hidden flex flex-col md:flex-row md:items-center gap-6">
          <div className="absolute -top-16 -right-16 w-72 h-72 bg-green-600 rounded-full opacity-30 blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 w-64 h-64 bg-blue-600 rounded-full opacity-20 blur-3xl" />
          <div className="relative flex-1">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-green-400">Mayoristas</span>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight mt-1">¿Sos revendedor o comercio?</h2>
            <p className="mt-2 text-white/80 max-w-xl text-sm md:text-base">Tu vendedor te arma un presupuesto mayorista con precios especiales y descuentos por volumen. Pedís por WhatsApp y coordinamos la entrega.</p>
          </div>
          <a
            href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(wholesaleMsg)}`}
            target="_blank" rel="noreferrer"
            className="relative inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-slate-900 font-bold rounded-full px-7 py-3.5 transition-colors self-start md:self-auto"
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" /> Escribir por WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}
